import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Text } from '@app/components/core/typography/Text'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@app/components/core/button/Button'
import { MultiLineTextInput } from '@app/components/core/input/text/MultiLineTextInput'
import { useTranslations } from '@i18n/useLanguage'
import { ParagraphedText } from '@app/components/core/typography/ParagraphedText'
import { EditButton } from '@app/components/core/button/EditButton'

export type TextEditPanelProps = {
  title: string
  editHint?: string
  value: string
  onSave: (value: string) => Promise<boolean>
}

export const TextEditPanel = ({
  title,
  editHint,
  value,
  onSave,
}: TextEditPanelProps) => {
  const translations = useTranslations().app.common
  const [editMode, setEditMode] = useState(false)
  const [editedValue, setEditedValue] = useState(value)
  const showHint = editHint && editMode
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const save = useCallback(() => {
    // TODO: loader
    void onSave(editedValue).then((success) => {
      if (success) {
        setEditMode(false)
      }
    })
  }, [editedValue, onSave])

  const cancel = useCallback(() => {
    setEditedValue(value)
    setEditMode(false)
  }, [value])

  useEffect(() => {
    if (editMode && textAreaRef.current) {
      const textArea = textAreaRef.current
      textArea.focus()

      // move cursor at the end
      const textLength = textArea.textLength
      textArea.selectionStart = textLength
      textArea.selectionEnd = textLength
    }
  }, [editMode])

  return (
    <Column
      alignItems="stretch"
      style={{
        backgroundColor: 'var(--palette-white)',
        padding: '1.5rem',
        borderRadius: '1.25rem',
      }}
    >
      <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Title.H4 style={{ marginBottom: '0' }}>{title}</Title.H4>
        <EditButton isVisible={!editMode} onClick={() => setEditMode(true)} />
      </Row>
      <Column gap="0.4rem">
        {!editMode && (
          <Text.Medium
            color="var(--palette-blue-500)"
            style={{ whiteSpace: 'pre-line' }}
          >
            <ParagraphedText>{value}</ParagraphedText>
          </Text.Medium>
        )}
        {showHint && (
          <Text.Medium color="var(--palette-blue-500)">{editHint}</Text.Medium>
        )}
        {editMode && (
          <MultiLineTextInput
            ref={textAreaRef}
            minHeight="13rem"
            value={editedValue}
            setValue={setEditedValue}
          />
        )}
      </Column>
      {editMode && (
        <Row style={{ justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            label={translations.cancel}
            onClick={cancel}
          />
          <Button variant="primary" label={translations.save} onClick={save} />
        </Row>
      )}
    </Column>
  )
}
